import { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { supabase } from '@/integrations/supabase/client';
import { AlertCircle, DollarSign, FileText, Users, BookOpen } from 'lucide-react';

interface RecordJournalDataFieldsProps {
  data: Record<string, any>;
  updateField: (field: string, value: any) => void;
  unaId: string;
  hideTypeSelection?: boolean;
}

const RECORD_TYPES = [
  { value: 'Private Contribution Received', label: 'Private Contribution Received', icon: DollarSign, description: 'Record a contribution received from a member or external party' },
  { value: 'Grant or Distribution Made', label: 'Grant or Distribution Made', icon: DollarSign, description: 'Document a grant or distribution to a beneficiary' },
  { value: 'Expense Payment', label: 'Expense Payment', icon: FileText, description: 'Log an expense payment to a vendor or service provider' },
  { value: 'Activity Log Entry', label: 'Activity Log Entry', icon: BookOpen, description: 'Create an activity record for general documentation' },
  { value: 'General Record', label: 'General Record', icon: FileText, description: 'File a general record for future reference' },
];

const CONTRIBUTION_PURPOSES = ['General Operating', 'Specific Project', 'Asset Acquisition', 'Ministry Support', 'Other'];
const CONTRIBUTION_METHODS = ['Cash', 'Check', 'Wire Transfer', 'ACH', 'Asset Transfer', 'Cryptocurrency', 'Other'];
const DISTRIBUTION_METHODS = ['Check', 'Wire Transfer', 'ACH', 'Direct Payment', 'Cash', 'Other'];
const EXPENSE_CATEGORIES = ['Housing', 'Utilities', 'Insurance', 'Maintenance & Repairs', 'Professional Services', 'Office & Administrative', 'Travel', 'Ministry Expenses', 'Other'];
const PAYMENT_METHODS = ['Check', 'Wire Transfer', 'ACH', 'Credit Card', 'Debit Card', 'Cash', 'Other'];
const ACTIVITY_CATEGORIES = ['Administrative', 'Governance', 'Financial', 'Asset Management', 'Communication', 'Legal', 'Ministry', 'Other'];
const RECORD_CATEGORIES = ['Governance', 'Financial', 'Asset', 'Membership', 'Legal', 'Compliance', 'Historical', 'Other'];

export function RecordJournalDataFields({ data, updateField, unaId, hideTypeSelection = false }: RecordJournalDataFieldsProps) {
  const [unaMembers, setUnaMembers] = useState<any[]>([]);
  const [treasurySecretary, setTreasurySecretary] = useState<any>(null);

  useEffect(() => {
    if (unaId) {
      loadUnaData();
    }
  }, [unaId]);

  useEffect(() => {
    // Set default record_date to today if not set
    if (!data.record_date) {
      updateField('record_date', new Date().toISOString().split('T')[0]);
    }
  }, []);

  const loadUnaData = async () => {
    // Load UNA members
    const { data: members } = await supabase
      .from('una_members')
      .select('id, full_name, email, member_role, role')
      .eq('una_id', unaId)
      .eq('status', 'active')
      .order('full_name');

    setUnaMembers(members || []);

    // Load Secretary Treasurer
    const { data: unaData } = await supabase
      .from('unas')
      .select('treasury_secretary_id')
      .eq('id', unaId)
      .single();

    if (unaData?.treasury_secretary_id) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('id, full_name, email, address, city, state, zip, country')
        .eq('id', unaData.treasury_secretary_id)
        .single();

      if (profile) {
        setTreasurySecretary(profile);
        // Auto-set authorized_by if not set
        if (!data.authorized_by) {
          updateField('authorized_by', profile.full_name);
        }
        
        // Auto-set contributor_address from profile if not set
        if (!data.contributor_address && profile.address) {
          const addressParts = [
            profile.address,
            [profile.city, profile.state, profile.zip].filter(Boolean).join(', ')
          ].filter(Boolean);
          if (profile.country && profile.country !== 'US') {
            addressParts.push(profile.country);
          }
          updateField('contributor_address', addressParts.join(', '));
        }
      }
    }
  };

  const recordType = data.record_type || '';
  const selectedTypeInfo = RECORD_TYPES.find(t => t.value === recordType);

  // Get all eligible signers for Resolution
  const getAllSigners = () => {
    const signers: { id: string; name: string; role: string }[] = [];
    
    // Add Secretary Treasurer
    if (treasurySecretary) {
      signers.push({
        id: treasurySecretary.id,
        name: treasurySecretary.full_name,
        role: 'Primary Officer'
      });
    }
    
    // Add UNA members
    unaMembers.forEach(member => {
      signers.push({
        id: member.id,
        name: member.full_name,
        role: member.role || member.member_role || 'Member'
      });
    });
    
    return signers;
  };

  return (
    <div className="space-y-6">
      {/* Record Type Selection - only shown if not hidden */}
      {!hideTypeSelection && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Record Type</CardTitle>
            <CardDescription>Select the type of record you want to create</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3">
              {RECORD_TYPES.map((type) => {
                const Icon = type.icon;
                const isSelected = recordType === type.value;
                return (
                  <div
                    key={type.value}
                    className={`flex items-start gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                      isSelected ? 'border-primary bg-primary/5' : 'hover:border-muted-foreground/50'
                    }`}
                    onClick={() => updateField('record_type', type.value)}
                  >
                    <div className={`p-2 rounded-lg ${isSelected ? 'bg-primary/10' : 'bg-muted'}`}>
                      <Icon className={`h-5 w-5 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
                    </div>
                    <div className="flex-1">
                      <p className={`font-medium ${isSelected ? 'text-primary' : ''}`}>{type.label}</p>
                      <p className="text-sm text-muted-foreground">{type.description}</p>
                    </div>
                    {isSelected && <Badge variant="default">Selected</Badge>}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Common Fields */}
      {recordType && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="record_date">Record Date *</Label>
                <Input
                  id="record_date"
                  type="date"
                  value={data.record_date || ''}
                  onChange={(e) => updateField('record_date', e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="record_title">Record Title *</Label>
                <Input
                  id="record_title"
                  value={data.record_title || ''}
                  onChange={(e) => updateField('record_title', e.target.value)}
                  placeholder="Brief description of this record"
                  required
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Private Contribution Fields */}
      {recordType === 'Private Contribution Received' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Contribution Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="amount">Contribution Amount *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    min="0"
                    className="pl-7"
                    value={data.amount || ''}
                    onChange={(e) => updateField('amount', parseFloat(e.target.value) || 0)}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="contributor_name">Contributor Name *</Label>
                <Input
                  id="contributor_name"
                  value={data.contributor_name || ''}
                  onChange={(e) => updateField('contributor_name', e.target.value)}
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="contributor_address">Contributor Address (for receipt)</Label>
              <Input
                id="contributor_address"
                value={data.contributor_address || ''}
                onChange={(e) => updateField('contributor_address', e.target.value)}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Contribution Purpose *</Label>
                <Select value={data.contribution_purpose || ''} onValueChange={(v) => updateField('contribution_purpose', v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select purpose" />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTRIBUTION_PURPOSES.map(p => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Contribution Method *</Label>
                <Select value={data.contribution_method || ''} onValueChange={(v) => updateField('contribution_method', v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select method" />
                  </SelectTrigger>
                  <SelectContent>
                    {CONTRIBUTION_METHODS.map(m => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="transaction_description">Additional Notes</Label>
              <Textarea
                id="transaction_description"
                value={data.transaction_description || ''}
                onChange={(e) => updateField('transaction_description', e.target.value)}
                rows={3}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Grant/Distribution Fields */}
      {recordType === 'Grant or Distribution Made' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <DollarSign className="h-5 w-5" />
              Distribution Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="amount">Distribution Amount *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    min="0"
                    className="pl-7"
                    value={data.amount || ''}
                    onChange={(e) => updateField('amount', parseFloat(e.target.value) || 0)}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="recipient_name">Recipient Name *</Label>
                <Input
                  id="recipient_name"
                  value={data.recipient_name || ''}
                  onChange={(e) => updateField('recipient_name', e.target.value)}
                  required
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="recipient_relationship">Recipient Relationship</Label>
              <Input
                id="recipient_relationship"
                value={data.recipient_relationship || ''}
                onChange={(e) => updateField('recipient_relationship', e.target.value)}
                placeholder="e.g., Family Member, Ministry Partner, Beneficiary"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="distribution_purpose">Purpose of Distribution *</Label>
              <Input
                id="distribution_purpose"
                value={data.distribution_purpose || ''}
                onChange={(e) => updateField('distribution_purpose', e.target.value)}
                placeholder="e.g., Housing provision, Education support, Ministry funding"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Distribution Method *</Label>
                <Select value={data.distribution_method || ''} onValueChange={(v) => updateField('distribution_method', v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select method" />
                  </SelectTrigger>
                  <SelectContent>
                    {DISTRIBUTION_METHODS.map(m => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="authorization_reference">Authorization Reference</Label>
                <Input
                  id="authorization_reference"
                  value={data.authorization_reference || ''}
                  onChange={(e) => updateField('authorization_reference', e.target.value)}
                  placeholder="e.g., Resolution #2024-05, Board approval 10/15/2024"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="transaction_description">Additional Notes</Label>
              <Textarea
                id="transaction_description"
                value={data.transaction_description || ''}
                onChange={(e) => updateField('transaction_description', e.target.value)}
                rows={3}
              />
            </div>
          </CardContent>
        </Card>
      )}

      {/* Expense Payment Fields */}
      {recordType === 'Expense Payment' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Expense Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="amount">Payment Amount *</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                  <Input
                    id="amount"
                    type="number"
                    step="0.01"
                    min="0"
                    className="pl-7"
                    value={data.amount || ''}
                    onChange={(e) => updateField('amount', parseFloat(e.target.value) || 0)}
                    required
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="payee_name">Payee / Vendor Name *</Label>
                <Input
                  id="payee_name"
                  value={data.payee_name || ''}
                  onChange={(e) => updateField('payee_name', e.target.value)}
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Expense Category *</Label>
                <Select value={data.expense_category || ''} onValueChange={(v) => updateField('expense_category', v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPENSE_CATEGORIES.map(c => (
                      <SelectItem key={c} value={c}>{c}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Payment Method *</Label>
                <Select value={data.payment_method || ''} onValueChange={(v) => updateField('payment_method', v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select method" />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map(m => (
                      <SelectItem key={m} value={m}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="is_recurring"
                checked={data.is_recurring || false}
                onCheckedChange={(checked) => updateField('is_recurring', checked)}
              />
              <Label htmlFor="is_recurring">This is a recurring expense</Label>
            </div>

            <div className="space-y-2">
              <Label htmlFor="transaction_description">Payment Description</Label>
              <Textarea
                id="transaction_description"
                value={data.transaction_description || ''}
                onChange={(e) => updateField('transaction_description', e.target.value)}
                rows={3}
              />
            </div>
          </CardContent>
        </Card>
      )}


      {/* Activity Log Fields */}
      {recordType === 'Activity Log Entry' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              Activity Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="activity_description">Activity Description *</Label>
              <Textarea
                id="activity_description"
                value={data.activity_description || ''}
                onChange={(e) => updateField('activity_description', e.target.value)}
                placeholder="Describe the activity, event, or action being recorded..."
                rows={5}
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Activity Category *</Label>
              <Select value={data.activity_category || ''} onValueChange={(v) => updateField('activity_category', v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {ACTIVITY_CATEGORIES.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Parties Involved</Label>
              <div className="border rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto">
                {[...(treasurySecretary ? [{ id: treasurySecretary.id, full_name: treasurySecretary.full_name, role: 'Primary Officer' }] : []), ...unaMembers].map((member) => (
                  <div key={member.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`party-${member.id}`}
                      checked={(data.parties_involved || []).includes(member.id)}
                      onCheckedChange={(checked) => {
                        const current = data.parties_involved || [];
                        if (checked) {
                          updateField('parties_involved', [...current, member.id]);
                        } else {
                          updateField('parties_involved', current.filter((id: string) => id !== member.id));
                        }
                      }}
                    />
                    <Label htmlFor={`party-${member.id}`}>{member.full_name}</Label>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="follow_up_required"
                checked={data.follow_up_required || false}
                onCheckedChange={(checked) => updateField('follow_up_required', checked)}
              />
              <Label htmlFor="follow_up_required">Follow-up action required</Label>
            </div>

            {data.follow_up_required && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border rounded-lg bg-muted/30">
                <div className="space-y-2">
                  <Label htmlFor="follow_up_date">Follow-up Date *</Label>
                  <Input
                    id="follow_up_date"
                    type="date"
                    value={data.follow_up_date || ''}
                    onChange={(e) => updateField('follow_up_date', e.target.value)}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="follow_up_notes">Follow-up Notes</Label>
                  <Input
                    id="follow_up_notes"
                    value={data.follow_up_notes || ''}
                    onChange={(e) => updateField('follow_up_notes', e.target.value)}
                    placeholder="What needs to be done?"
                  />
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* General Record Fields */}
      {recordType === 'General Record' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Record Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="record_content">Record Content *</Label>
              <Textarea
                id="record_content"
                value={data.record_content || ''}
                onChange={(e) => updateField('record_content', e.target.value)}
                placeholder="Enter the full content of this record..."
                rows={8}
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Record Category *</Label>
              <Select value={data.record_category || ''} onValueChange={(v) => updateField('record_category', v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {RECORD_CATEGORIES.map(c => (
                    <SelectItem key={c} value={c}>{c}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="is_confidential"
                checked={data.is_confidential || false}
                onCheckedChange={(checked) => updateField('is_confidential', checked)}
              />
              <Label htmlFor="is_confidential">Mark as confidential (restricted access)</Label>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Internal Notes (always visible if record type selected) */}
      {recordType && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Internal Notes</CardTitle>
            <CardDescription>Private notes for your reference (not included in official record)</CardDescription>
          </CardHeader>
          <CardContent>
            <Textarea
              value={data.internal_notes || ''}
              onChange={(e) => updateField('internal_notes', e.target.value)}
              placeholder="Private notes for your reference..."
              rows={3}
            />
          </CardContent>
        </Card>
      )}
    </div>
  );
}
